import React, { useEffect, useRef } from 'react';
import { X, Keyboard } from 'lucide-react';
import { getShortcutsList } from './useKeyboardShortcuts';

/**
 * KeyboardShortcutsPanel - Modal showing all available keyboard shortcuts
 *
 * @param {Object} props
 * @param {Function} props.onClose - Callback to close the panel
 */
export default function KeyboardShortcutsPanel({ onClose }) {
  const modalRef = useRef(null);
  const shortcuts = getShortcutsList();

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Focus trap and initial focus
  useEffect(() => {
    const modal = modalRef.current;
    if (modal) {
      const closeButton = modal.querySelector('.workbench-modal-close');
      if (closeButton) {
        closeButton.focus();
      }
    }
  }, []);

  // Click outside to close
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="workbench-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="shortcuts-modal-title"
    >
      <div
        ref={modalRef}
        className="workbench-modal shortcuts-modal"
        role="document"
      >
        <div className="workbench-modal-header">
          <div className="shortcuts-header-content">
            <Keyboard size={20} aria-hidden="true" />
            <h2 id="shortcuts-modal-title">Keyboard Shortcuts</h2>
          </div>
          <button
            className="workbench-modal-close"
            onClick={onClose}
            aria-label="Close keyboard shortcuts panel"
          >
            <X size={20} />
          </button>
        </div>

        <div className="workbench-modal-body">
          <p className="shortcuts-description">
            Use these shortcuts to navigate and control the Dev Workbench more efficiently.
          </p>

          <div className="shortcuts-list" role="list">
            {shortcuts.map((shortcut, index) => (
              <div key={index} className="shortcut-item" role="listitem">
                <kbd className="shortcut-keys">{shortcut.keys}</kbd>
                <span className="shortcut-description">{shortcut.description}</span>
              </div>
            ))}
          </div>

          <div className="shortcuts-tip">
            <strong>Tip:</strong> Press <kbd>?</kbd> anytime to toggle this panel.
          </div>
        </div>
      </div>
    </div>
  );
}
